import { KeyValue } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AuthService } from '../core/data/auth.service';
import { PatternConstants } from '../core/data/pattern-constants';
import { IUserAccount } from '../core/data/user-account';
import { UserAccountService } from '../core/data/user-account.service';
import { IsUniqueValidator } from '../shared/directives/is-unique-validator.directive';
import { ValidationHelper } from '../shared/validation-helper';

const tempDate = new Date();
interface ICreateAccount {
    formSubmitError: string[];
    pathToTerms: string;
    maxDate: Date;
    terms: string;
    version: string;
    termsVisible: boolean;
    isAllowed: boolean;
    allowedEmailDomains: string[];
    studentIdRequired: boolean;
    studentIdEditable: boolean;
    consumerBirthDateValidation: boolean;
}
const createAccount: ICreateAccount = {
    formSubmitError: [],
    pathToTerms: '',
    maxDate: new Date(tempDate.getUTCFullYear(), tempDate.getUTCMonth(), tempDate.getUTCDate(), 0, 0, 0, 0),
    terms: '',
    version: 'loading...',
    termsVisible: false,
    isAllowed: false,
    allowedEmailDomains: [],
    studentIdRequired: false,
    studentIdEditable: true,
    consumerBirthDateValidation: false
} as ICreateAccount;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ss-create-account',
    templateUrl: './create-account.component.html',
    styles: [
    ]
})
export class CreateAccountComponent {
    createAccountFormGroup: UntypedFormGroup;
    validationHelper = new ValidationHelper();
    private createAccount$ = new BehaviorSubject(createAccount);

    vm$ = combineLatest([
        this.authService.getAuthInfo(),
        this.userAccountService.getAccountCreationSettings(),
        this.createAccount$
    ]).pipe(
        tap(([, accountCreationSettings, accountCreation]) => {
            const validateStudent
                = this.getSettingValue<boolean>((accountCreationSettings as unknown as KeyValue<string, any>[]), 'ValidateStudent');

            // Set max date of birth to allow 14 years of age or older if Consumer Site setting is ON 
            const consumerBirthDateValidation = this.getSettingValue<boolean>((accountCreationSettings as unknown as KeyValue<string, any>[]), 'EnableConsumerSite');
            accountCreation.consumerBirthDateValidation = consumerBirthDateValidation;
            if (consumerBirthDateValidation) {
                accountCreation.maxDate = new Date(tempDate.getUTCFullYear() - 14, tempDate.getUTCMonth(), tempDate.getUTCDate(), 0, 0, 0, 0)
            }

            // Student ID must be captured if we don't validate the student.
            // Otherwise a blank student ID in this case would prevent the VerifiedStudent record from being created.
            accountCreation.studentIdEditable = this.getSettingValue<boolean>((accountCreationSettings as unknown as KeyValue<string, any>[]), 'StudentIdEditable');
            accountCreation.studentIdRequired
                = !validateStudent ||
                (this.getSettingValue<boolean>((accountCreationSettings as unknown as KeyValue<string, any>[]), 'StudentIdRequired')
                    && accountCreation.studentIdEditable);

            const allowedDomains
                = this.getSettingValue<string>((accountCreationSettings as unknown as KeyValue<string, any>[]), 'AllowedEmailDomains');

            accountCreation.allowedEmailDomains = (allowedDomains !== null && allowedDomains !== '')
                ? allowedDomains.split(' ')
                : [];

            this.createAccountFormGroup = new UntypedFormGroup({
                email: new UntypedFormControl('',
                    [Validators.required,
                    Validators.maxLength(128),
                    Validators.pattern(PatternConstants.email)]),
                confirmEmail: new UntypedFormControl('', [Validators.required]),
                username: new UntypedFormControl('',
                    [Validators.required,
                    Validators.minLength(8), Validators.maxLength(64),
                    Validators.pattern(PatternConstants.username)],
                    [IsUniqueValidator.createUniqueValidator(this.userAccountService, false)]
                ),
                password: new UntypedFormControl('', [Validators.required, Validators.pattern(PatternConstants.password)]),
                confirmPassword: new UntypedFormControl('', [Validators.required]),
                firstName: new UntypedFormControl('', [Validators.required]),
                lastName: new UntypedFormControl('', [Validators.required]),
                dateOfBirth: new UntypedFormControl(null, [Validators.required]),
                phoneNumber: new UntypedFormControl(''),
                studentId: new UntypedFormControl('', [Validators.maxLength(100),
                conditionalValidator(() => accountCreation.studentIdRequired === true, Validators.required)],
                    [IsUniqueValidator.createUniqueValidator(this.userAccountService, true)]
                ),
                termsAccepted: new UntypedFormControl(false, [Validators.requiredTrue])
            });
        }),
        map(
            ([
                authInfo,
                accountCreationSettings,
                accountCreation
            ]) => (
                this.validationHelper
                    .setDependentValues(accountCreation.allowedEmailDomains)
                    .isConsumerBirthDateValidation(accountCreation.consumerBirthDateValidation)
                    .setErrorMapping(),
                {
                    ...accountCreation,
                    isAllowed: authInfo.allowCreateAccount,
                    terms: this.getSettingValue<string>((accountCreationSettings as unknown as KeyValue<string, any>[]), 'Terms'),
                    version: authInfo.version
                }
            ))
    );
    constructor(
        private router: Router,
        private authService: AuthService,
        private userAccountService: UserAccountService
    ) {
    }

    onSave() {
        if (this.createAccountFormGroup.invalid)
            return;

        this.userAccountService.add({ ...this.createAccountFormGroup.value } as IUserAccount).subscribe(
            () => {
                const values = this.createAccount$.getValue();
                this.createAccount$.next({ ...values, formSubmitError: [] });
                this.router.navigateByUrl('/');

            }, (err: HttpErrorResponse) => {

                const apiError: string = err.error;
                const values = this.createAccount$.getValue();
                this.createAccount$.next({ ...values, formSubmitError: [] });
                const formSubmitErrorArr: string[] = [];

                if (values.formSubmitError.indexOf(apiError) === -1)
                    formSubmitErrorArr.push(apiError);

                this.createAccount$.next({ ...values, formSubmitError: formSubmitErrorArr });
                window.scrollTo(0, 0);
            });
    }

    getSettingValue = <T>(settings: KeyValue<string, any>[], settingName: string): T => settings.find(x => x.key === settingName)?.value;

    getErrorMessages = (textField: string): string[] => this.validationHelper.getValidationMessage(this.createAccountFormGroup, textField);
}

function conditionalValidator(condition: (() => boolean), validator: ValidatorFn): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!condition())
            return null;

        return validator(control);
    };
}
